import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, API, token } from "@/lib/api";
import { dateFmt, formatErr } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
    Loader2, RefreshCw, MailCheck, FileText, Trash2, ExternalLink,
    AlertTriangle, Inbox, FileDown,
} from "lucide-react";

export default function ReviewEmailInvoices() {
    const navigate = useNavigate();
    const [rows, setRows] = useState(null);
    const [cfg, setCfg] = useState(null);
    const [busy, setBusy] = useState("");
    const [fetching, setFetching] = useState(false);

    const load = () => api.get("/email-inbox/messages").then((r) => setRows(r.data)).catch(() => setRows([]));
    useEffect(() => {
        load();
        api.get("/tenant/email-inbox").then((r) => setCfg(r.data)).catch(() => setCfg({ configured: false }));
    }, []);

    const fetchNew = async () => {
        setFetching(true);
        try {
            const { data } = await api.post("/email-inbox/fetch");
            data.added > 0 ? toast.success(data.message) : toast.info(data.message);
            load();
        } catch (err) { toast.error(formatErr(err.response?.data?.detail)); } finally { setFetching(false); }
    };
    const importMsg = async (m) => {
        setBusy(m.id);
        try {
            const { data } = await api.post(`/email-inbox/messages/${m.id}/import`);
            toast.success("Imported — invoice is now Awaiting Review");
            navigate(`/app/invoices/${data.invoice_id}`);
        } catch (err) { toast.error(formatErr(err.response?.data?.detail)); setBusy(""); }
    };
    const del = async (m) => {
        setBusy(m.id);
        try { await api.delete(`/email-inbox/messages/${m.id}`); toast.success("Email deleted"); load(); }
        catch (err) { toast.error(formatErr(err.response?.data?.detail)); } finally { setBusy(""); }
    };
    const viewFile = (m) => window.open(`${API}/email-inbox/messages/${m.id}/file?auth=${token()}`, "_blank");

    return (
        <div className="space-y-6 pe-fade-up" data-testid="review-email-invoices">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-brand-emerald flex items-center gap-2">
                        <MailCheck className="h-6 w-6 text-brand-teal" /> Review Email Invoices
                    </h1>
                    <p className="text-muted-foreground text-sm mt-1">
                        Emails with PDF attachments from your connected mailbox land here. Import the invoices; delete anything that isn't one.
                    </p>
                </div>
                <Button disabled={fetching} onClick={fetchNew} className="bg-brand-emerald hover:bg-brand-emeraldhover rounded-lg" data-testid="fetch-emails-button">
                    {fetching ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Checking…</> : <><RefreshCw className="h-4 w-4 mr-1" /> Check for new emails</>}
                </Button>
            </div>

            {cfg && !cfg.configured && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 text-amber-900 p-4 text-sm flex items-start gap-2" data-testid="email-not-configured">
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>No invoice mailbox is connected yet. Connect one in <Link to="/app/settings" className="underline font-medium">School Settings → Email Inbox</Link> to start receiving vendor invoices here.</span>
                </div>
            )}
            {cfg && cfg.configured && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 text-amber-900 p-3 text-xs flex items-center gap-2" data-testid="email-mocked-banner">
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    <span>Connected to <b>{cfg.mailbox || cfg.provider}</b> · {cfg.folder || "Inbox"}. Live mail sync is <b>simulated (MOCKED)</b> — fetch pulls demo emails until credentials are wired to live mail.</span>
                </div>
            )}

            <div className="bg-white border border-border rounded-xl overflow-hidden">
                {rows === null ? (
                    <div className="grid place-items-center py-16 text-brand-emerald"><Loader2 className="h-6 w-6 animate-spin" /></div>
                ) : rows.length === 0 ? (
                    <div className="py-16 text-center text-muted-foreground"><Inbox className="h-8 w-8 mx-auto mb-3 opacity-40" /> No emails to review. Click <b>Check for new emails</b> to pull the latest.</div>
                ) : (
                    <div className="overflow-x-auto pe-scroll">
                        <table className="w-full text-sm" data-testid="email-staging-table">
                            <thead><tr className="text-left text-muted-foreground border-b border-border bg-secondary/40">
                                <th className="p-3 font-medium">From</th>
                                <th className="p-3 font-medium">Subject</th>
                                <th className="p-3 font-medium">Attachment</th>
                                <th className="p-3 font-medium">Received</th>
                                <th className="p-3 font-medium text-right">Actions</th>
                            </tr></thead>
                            <tbody>
                                {rows.map((m) => (
                                    <tr key={m.id} className="border-b border-border last:border-0 hover:bg-secondary/40" data-testid={`email-row-${m.id}`}>
                                        <td className="p-3">
                                            <div className="font-medium text-brand-emerald">{m.from_name}</div>
                                            <div className="text-xs text-muted-foreground">{m.from_email}</div>
                                        </td>
                                        <td className="p-3 max-w-[240px] truncate" title={m.subject}>{m.subject}</td>
                                        <td className="p-3">
                                            <button onClick={() => viewFile(m)} className="inline-flex items-center gap-1.5 text-brand-teal hover:underline font-mono text-xs" data-testid={`email-view-${m.id}`}>
                                                <FileText className="h-3.5 w-3.5" /> {m.attachment_filename} <ExternalLink className="h-3 w-3" />
                                            </button>
                                        </td>
                                        <td className="p-3 text-muted-foreground">{dateFmt(m.received_at)}</td>
                                        <td className="p-3">
                                            <div className="flex items-center justify-end gap-2">
                                                <Button size="sm" disabled={busy === m.id} onClick={() => importMsg(m)} className="bg-brand-emerald hover:bg-brand-emeraldhover rounded-lg" data-testid={`email-import-${m.id}`}>
                                                    {busy === m.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FileDown className="h-4 w-4 mr-1" /> Import</>}
                                                </Button>
                                                <Button size="sm" variant="ghost" disabled={busy === m.id} onClick={() => del(m)} className="text-rose-600" data-testid={`email-delete-${m.id}`}>
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
